import { useRef } from "react";
import { Upload, Library, Loader2, X } from "lucide-react";

const SOURCE_LABELS = {
  mockup_template: "Mockup Template",
  detail_crop: "Detail Crop",
  room_scene: "Room Scene",
  artwork: "Artwork",
  manual_upload: "Manual Upload",
  generated_from_artwork: "Generated",
};

const ROLE_COLORS = {
  hero: "text-[#CBA052] border-[#CBA052]/30",
  gallery: "text-blue-400 border-blue-400/30",
  detail: "text-purple-400 border-purple-400/30",
  thumbnail: "text-green-400 border-green-400/30",
};

export function SlotStatus({ required, hasAsset }) {
  if (hasAsset) return <span className="w-3.5 h-3.5 rounded-full bg-green-500 inline-block shrink-0" />;
  if (required) return <span className="w-3.5 h-3.5 rounded-full bg-red-500/60 inline-block shrink-0" />;
  return <span className="w-3.5 h-3.5 rounded-full bg-white/10 inline-block shrink-0" />;
}

export default function PresentationSlotCard({ slot, data, onUpdateSlot, onPickFromLibrary, onUpload }) {
  const fileRef = useRef(null);
  const hasAsset = !!(data?.asset_url || "").trim();
  const isUploading = !!data?._uploading;
  const roleColor = ROLE_COLORS[slot.storefront_role] || "text-white/30 border-white/10";

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onUpload(slot.key, file);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div
      className={`border rounded p-3 ${hasAsset ? "border-green-500/20 bg-green-500/5" : slot.required ? "border-red-500/15 bg-red-500/5" : "border-white/[0.06] bg-white/[0.01]"}`}
      data-testid={`slot-${slot.key}`}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5"><SlotStatus required={slot.required} hasAsset={hasAsset} /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-xs font-semibold text-white">{slot.label}</span>
            {slot.required && (
              <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border bg-red-500/10 text-red-400 border-red-500/20">Required</span>
            )}
            <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${roleColor}`}>
              {slot.storefront_role}
            </span>
            <span className="text-[9px] text-white/25 ml-auto">{slot.aspect_ratio}</span>
          </div>

          {slot.description && <p className="text-[10px] text-white/30 mb-2">{slot.description}</p>}

          {hasAsset && (
            <div className="relative mb-2 w-24 h-16 bg-black rounded overflow-hidden border border-white/10 group">
              <img src={data.asset_url} alt={slot.label} className="w-full h-full object-cover" />
              <button
                onClick={() => onUpdateSlot(slot.key, "asset_url", "")}
                className="absolute top-0.5 right-0.5 w-4 h-4 bg-black/80 rounded flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X size={8} className="text-white" />
              </button>
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={isUploading}
              className="flex items-center gap-1 text-[10px] font-medium text-[#CBA052] border border-[#CBA052]/30 px-2 py-1 rounded hover:bg-[#CBA052]/10 disabled:opacity-50 transition-colors"
              data-testid={`slot-upload-btn-${slot.key}`}
            >
              {isUploading ? <Loader2 size={10} className="animate-spin" /> : <Upload size={10} />}
              {isUploading ? "Uploading..." : "Upload"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
            <button
              onClick={() => onPickFromLibrary(slot.key)}
              className="flex items-center gap-1 text-[10px] text-white/40 border border-white/10 px-2 py-1 rounded hover:text-white/70 hover:border-white/20 transition-colors"
              data-testid={`slot-pick-btn-${slot.key}`}
            >
              <Library size={10} /> From Library
            </button>
          </div>

          <input
            value={data?.asset_url || ""}
            onChange={(e) => onUpdateSlot(slot.key, "asset_url", e.target.value)}
            placeholder="or paste image URL..."
            className="mt-2 w-full bg-white/[0.03] border border-white/[0.06] text-white text-[10px] rounded px-2.5 py-1.5 outline-none focus:border-white/20"
            data-testid={`slot-url-${slot.key}`}
          />

          <div className="mt-1 flex items-center gap-1">
            <span className="text-[9px] text-white/20">Expected:</span>
            <span className="text-[9px] text-white/30">{SOURCE_LABELS[slot.source_type] || slot.source_type}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
